import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Button, ErrorNote, Input } from "./ui";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /** What this will do, in the words of someone who has to live with it. */
  description: ReactNode;
  /** When set, the exact string that must be typed before confirming. */
  confirmPhrase?: string;
  confirmLabel: string;
  /**
   * Most confirmations here guard something destructive. A few guard something
   * consequential but wanted — recording a payment issues a licence and a
   * commission — and colouring those as danger teaches people to ignore red.
   */
  confirmVariant?: "danger" | "primary";
  pending?: boolean;
  error?: string | null;
  onConfirm: (typed: string) => void;
  onCancel: () => void;
}

/**
 * Built on the native `<dialog>` element rather than a portal and a stack of
 * effects. `showModal()` traps focus, closes on Escape, renders in the top
 * layer, and makes the rest of the page inert — all behaviours a hand-rolled
 * modal has to reimplement and usually gets subtly wrong.
 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmPhrase,
  confirmLabel,
  confirmVariant = "danger",
  pending = false,
  error = null,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const [typed, setTyped] = useState("");
  const descriptionId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (dialog === null) return;

    if (open && !dialog.open) {
      setTyped("");
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  const phraseMatches = confirmPhrase === undefined || typed.trim() === confirmPhrase;

  return (
    <dialog
      ref={ref}
      aria-labelledby={`${descriptionId}-title`}
      aria-describedby={descriptionId}
      // Escape fires `cancel`, so the parent's state has to follow or the
      // dialog closes while the app still believes it is open.
      onCancel={(event) => {
        event.preventDefault();
        onCancel();
      }}
      className="w-[min(30rem,calc(100vw-2rem))] rounded-lg border border-hairline-strong bg-surface p-0 text-ink backdrop:bg-base/80"
    >
      <form
        method="dialog"
        onSubmit={(event) => {
          event.preventDefault();
          if (phraseMatches && !pending) onConfirm(typed.trim());
        }}
        className="space-y-4 p-5"
      >
        <h2 id={`${descriptionId}-title`} className="text-base font-semibold">
          {title}
        </h2>
        <div id={descriptionId} className="space-y-3 text-sm text-muted">
          {description}
        </div>

        {confirmPhrase !== undefined && (
          <div className="space-y-1.5">
            <label className="field-label" htmlFor={`${descriptionId}-confirm`}>
              Type <span className="tabular text-ink">{confirmPhrase}</span> to confirm
            </label>
            <Input
              id={`${descriptionId}-confirm`}
              value={typed}
              autoComplete="off"
              spellCheck={false}
              className="tabular"
              onChange={(event) => setTyped(event.target.value)}
            />
          </div>
        )}

        {error !== null && <ErrorNote>{error}</ErrorNote>}

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button type="submit" variant={confirmVariant} disabled={!phraseMatches || pending}>
            {pending ? "Working…" : confirmLabel}
          </Button>
        </div>
      </form>
    </dialog>
  );
}
